import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Store, Code, Globe2, LayoutGrid } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';

export type IntegrationCategory = 'all' | 'messaging' | 'commerce' | 'webchat' | 'marketplaces';

interface CategoryTabsProps {
  activeCategory: IntegrationCategory;
  onSelectCategory: (category: IntegrationCategory) => void;
  counts: {
    messaging: number;
    commerce: number;
    total: number;
  };
}

export const CategoryTabs: React.FC<CategoryTabsProps> = ({
  activeCategory,
  onSelectCategory,
  counts,
}) => {
  const tabs = [
    {
      id: 'all' as IntegrationCategory,
      label: 'All Channels',
      icon: LayoutGrid,
      count: counts.total,
      totalPossible: 9,
      tooltip: `${counts.total} of 9 total channels connected`,
    },
    {
      id: 'messaging' as IntegrationCategory,
      label: 'Messaging',
      icon: MessageSquare,
      count: counts.messaging,
      totalPossible: 3,
      tooltip: `${counts.messaging} of 3 messaging channels connected (Messenger, Instagram, WhatsApp)`,
    },
    {
      id: 'commerce' as IntegrationCategory,
      label: 'Commerce Stores',
      icon: Store,
      count: counts.commerce,
      totalPossible: 6,
      tooltip: `${counts.commerce} of 6 commerce & marketplace stores connected (Shopify, WooCommerce, Amazon, Salla, eBay, Lazada)`,
    },
    {
      id: 'webchat' as IntegrationCategory,
      label: 'Web Chat Widget',
      icon: Code,
      badgeText: 'Active',
      tooltip: 'Embeddable website live chat widget',
    },
    {
      id: 'marketplaces' as IntegrationCategory,
      label: 'Marketplaces',
      icon: Globe2,
      badgeText: 'Soon',
      tooltip: 'Daraz, Lazada, Shopee, eBay, Mercado Libre (Coming soon)',
    },
  ];

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeCategory === tab.id;

        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onSelectCategory(tab.id)}
            className={cn(
              'relative flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2 text-xs font-semibold transition-all select-none',
              isActive
                ? 'text-primary dark:text-white'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100/60 dark:hover:bg-slate-800/60'
            )}
          >
            {isActive && (
              <motion.div
                layoutId="active-integration-category-tab"
                className="absolute inset-0 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/90 dark:border-slate-700/80 shadow-xs"
                transition={{ type: 'spring', stiffness: 500, damping: 35 }}
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              <Icon className={cn('h-3.5 w-3.5', isActive ? 'text-primary' : 'text-slate-400 dark:text-slate-500')} />
              <span>{tab.label}</span>

              {tab.tooltip ? (
                <Tooltip content={tab.tooltip} placement="top">
                  <span
                    className={cn(
                      'inline-flex items-center justify-center rounded-md px-1.5 py-0.2 text-[10px] font-bold tracking-tight border transition-colors',
                      isActive
                        ? tab.count && tab.count > 0
                          ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-200/80 dark:border-emerald-800/60'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600'
                        : 'bg-slate-100/70 dark:bg-slate-800/70 text-slate-500 dark:text-slate-400 border-slate-200/60 dark:border-slate-750'
                    )}
                  >
                    {tab.count !== undefined ? tab.count : tab.badgeText}
                  </span>
                </Tooltip>
              ) : (
                <span className="inline-flex items-center justify-center rounded-md px-1.5 py-0.2 text-[10px] font-bold tracking-tight bg-slate-100 dark:bg-slate-800 text-slate-500 border border-slate-200/60 dark:border-slate-700">
                  {tab.badgeText}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default CategoryTabs;
